








































































































































































































import {Component, Emit, Prop, Vue, Watch} from 'vue-property-decorator';
import {namespace} from 'vuex-class';
import {Order, OrderDimensions, OrderPriority, OrderTransportMode} from '@/interfaces/order.interface';
import {User} from '@/interfaces/user.interface';
import {COUNTRIES, CURRENCIES, ORDER_CLARIFICATIONS, VALIDATION_RULES} from '@/helpers/helpers';
import OrderFormCompanyFields from '@/components/OrderFormCompanyFields.vue';
import {Currency} from '@/interfaces/currency.interface';
import RichTextEditor from '@/components/RichTextEditor.vue';
import { ItemDetailsTemplate } from '@/interfaces/item-details-template.inteface';
import ItemDetailsTemplateDialog from "@/components/ItemDetailsTemplateDialog.vue";
import ItemDetailsTemplateMenu from "@/components/ItemDetailsTemplateMenu.vue";
import TextFieldWithTooltip from "@/components/TextFieldWithTooltip.vue";
import TextAreaWithTooltip from "@/components/TextAreaWithTooltip.vue";

const ordersModule = namespace('orders');
const usersModule = namespace('users');
const templatesModule = namespace('templates');

@Component({
  components: {
    OrderFormCompanyFields,
    RichTextEditor,
    ItemDetailsTemplateDialog,
    ItemDetailsTemplateMenu,
    TextFieldWithTooltip,
    TextAreaWithTooltip,
  }
})
export default class OrderForm extends Vue {
  @ordersModule.Action create!: any;
  @ordersModule.Action update!: any;
  @ordersModule.State priorities!: OrderPriority[];
  @ordersModule.State transportModes!: OrderTransportMode[];
  @usersModule.Getter currentUser!: User;
  @templatesModule.State itemDetailsTemplates!: ItemDetailsTemplate[];
  @templatesModule.Action fetchTemplates: any;
  @Prop({default: () => ({})}) updatableOrder: any;

  valid = false;

  getClarificationText(key: string): string {
    return ORDER_CLARIFICATIONS[key];
  }

  order: Order = {
    dimensions: [{}],
    shipper: {
      name: ''
    },
    consignee: {
      name: ''
    },
    customsCurrency: 'EUR',
    hasQuotesSubmitted: false,
    priority: {
      uuid: '8ce458b9-d205-47c1-9d1b-b43f4be182e7',
      name: 'Routine',
      rank: 3,
      details: 'during the week'
    },
    details: '',
  };

  requiredDimensionFieldRules =  [] as any[];
  requiredTextFieldRules = VALIDATION_RULES.required;
  emailFieldRules = VALIDATION_RULES.email;
  requiredNumberRules = [...VALIDATION_RULES.required, ...VALIDATION_RULES.number];

  ownCompany = 'Magnetic MRO AS';
  areCountriesInEU = true;

  mounted() {
    this.fetchTemplates();
    this.setDimensionsRules();
  }

  // TODO: Find a more elegant solution for an optional parameter
  @Watch('updatableOrder')
  orderInputUpdated(order: Order) {
    this.order = {
      ...{dimensions: [{}]},
      ...order
    };
  }

  @Watch('order.customs')
  updateCustoms(customs: boolean) {
    if (!customs) {
      this.order.customsValue = undefined;
    }
  }

  get orderDimensions(): OrderDimensions[] {
    return this.order && this.order.dimensions || [{}];
  }

  async handleSubmit() {
    if (this.valid) {
      this.isUpdateMode ? await this.updateOrder() : await this.createOrder();
      this.$router.back();
    } else {
      // @ts-ignore
      // noinspection TypeScriptUnresolvedFunction
      this.$refs.form.validate();
    }
  }

  get isUpdateMode(): boolean {
    return !!this.order.uuid;
  }

  private isOwnCompany(companyName: string | undefined): boolean {
    return companyName === this.ownCompany;
  }

  private setDimensionsRules() {
    const shipperCompanyName = this.order.shipper ? this.order.shipper.name : '';
    this.requiredDimensionFieldRules = (this.isOwnCompany(shipperCompanyName)) ? [] : VALIDATION_RULES.required;
  }

  private isCustomsSwitchDisabled(): boolean {
    return !this.areCountriesInEU;
  }

  private async createOrder() {
    await this.create({order: this.order});
  }

  private async updateOrder() {
    await this.update({order: this.order});
  }

  get currencies(): Currency[] {
    return CURRENCIES;
  }

  private setOrderDetails(itemDetails: string) {
    this.order.details = itemDetails;
  }

  createNewDimension(): void {
    if (!this.order.dimensions) this.order = {
      ...this.order,
      ...{dimensions: [{}]}
    };
    this.order!.dimensions!.push({});
  }

  maybeToggleCustoms(): void {
    let countryName: undefined | string = this.order && this.order.shipper && this.order.shipper.country;
    const shipperCountry: any = countryName && COUNTRIES.find(c => c.name === countryName);

    countryName = this.order && this.order.consignee && this.order.consignee.country;
    const cneeCountry: any = countryName && COUNTRIES.find(c => c.name === countryName);

    if(!shipperCountry || !cneeCountry) return;

    this.areCountriesInEU = shipperCountry.EU && cneeCountry.EU;

    // if order has customs manually applied, do not toggle it automatically
    if (this.order.customs) return;

    this.order = {
      ...this.order,
      ...{customs: !this.areCountriesInEU}
    };
  }

  // TODO: Figure out a better way to handle filling out non-locked fields
  updateShipperFields(): void {
    if (this.order && this.order.shipper) {
      if (this.isOwnCompany(this.order.shipper.name) && !this.order.createdAt) {
        this.order.shipperContactName = `${this.currentUser.firstName} ${this.currentUser.lastName}`;
        this.order.shipperContactEmail = this.currentUser.email;
        this.order.shipperContactPhone = this.currentUser.phoneNumber;
      } else {
        if(this.order.shipper.defaultContact) this.order.shipperContactName = this.order.shipper.defaultContact;
        if(this.order.shipper.defaultPhone) this.order.shipperContactPhone = this.order.shipper.defaultPhone;
      }
    }
    this.maybeToggleCustoms();
    this.setDimensionsRules();
  }

  updateDefaultShipperContactName(): void {
    if (this.order && this.order.shipper && this.order.shipperContactName) this.order.shipper.defaultContact = this.order.shipperContactName;
  }

  updateDefaultShipperPhone(): void {
    if (this.order && this.order.shipper && this.order.shipperContactPhone) this.order.shipper.defaultPhone = this.order.shipperContactPhone;
  }

  updateConsigneeFields(): void {
    if (this.order && this.order.consignee) {
      if (this.isOwnCompany(this.order.consignee.name) && !this.order.createdAt) {
        this.order.consigneeContactName = `${this.currentUser.firstName} ${this.currentUser.lastName}`;
        this.order.consigneeContactEmail = this.currentUser.email;
        this.order.consigneeContactPhone = this.currentUser.phoneNumber;
      } else {
        if(this.order.consignee.defaultContact) this.order.consigneeContactName = this.order.consignee.defaultContact;
        if(this.order.consignee.defaultPhone) this.order.consigneeContactPhone = this.order.consignee.defaultPhone;
      }
    }
    this.maybeToggleCustoms();
  }

  updateDefaultConsigneeContactName(): void {
    if (this.order && this.order.consignee && this.order.consigneeContactName) this.order.consignee.defaultContact = this.order.consigneeContactName;
  }

  updateDefaultConsigneePhone(): void {
    if (this.order && this.order.consignee && this.order.consigneeContactPhone) this.order.consignee.defaultPhone = this.order.consigneeContactPhone;
  }
}
